import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, Slider, Select, Space, Typography, Button, Alert } from 'antd';
import Cropper from 'react-easy-crop';
import 'react-easy-crop/react-easy-crop.css';

const { Text } = Typography;

// Helper to check if aspect ratio is close to square (within tolerance)
const isSquareAspect = (ratio, tolerance = 0.15) => ratio && Math.abs(ratio - 1) < tolerance;

const ASPECT_PRESETS = [
  { label: 'Free', value: 'free' },
  { label: 'Square (1:1)', value: 1 },
  { label: 'Landscape (16:9)', value: 16 / 9 },
  { label: 'Classic (4:3)', value: 4 / 3 },
  { label: 'Portrait (3:4)', value: 3 / 4 }
];

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.setAttribute('crossOrigin', 'anonymous');
    image.src = url;
  });

const getCroppedBlob = async (imageSrc, cropPixels, mimeType = 'image/jpeg') => {
  const image = await createImage(imageSrc);
  const crop = cropPixels || {
    x: 0,
    y: 0,
    width: image.naturalWidth,
    height: image.naturalHeight
  };

  const canvas = document.createElement('canvas');
  canvas.width = crop.width;
  canvas.height = crop.height;

  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    image,
    crop.x,
    crop.y,
    crop.width,
    crop.height,
    0,
    0,
    crop.width,
    crop.height
  );

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('Failed to crop image.'));
          return;
        }
        resolve(blob);
      },
      mimeType,
      0.92
    );
  });
};

const ImageCropModal = ({
  open,
  imageSrc,
  title = 'Adjust Image',
  instructions,
  aspect,
  allowAspectSelection = true,
  onCancel,
  onApply,
  onUseOriginal
}) => {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [selectedAspect, setSelectedAspect] = useState(aspect || 'original');
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [originalAspect, setOriginalAspect] = useState(null);

  useEffect(() => {
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setSelectedAspect(aspect || 'original');
    setCroppedAreaPixels(null);
  }, [open, imageSrc, aspect]);

  useEffect(() => {
    if (allowAspectSelection && selectedAspect === 'original' && !originalAspect) {
      setSelectedAspect('free');
    }
  }, [allowAspectSelection, originalAspect, selectedAspect]);

  const aspectOptions = useMemo(() => {
    if (!allowAspectSelection) {
      return [];
    }

    const options = [...ASPECT_PRESETS];
    if (originalAspect) {
      options.splice(1, 0, { label: 'Original', value: 'original' });
    }
    return options;
  }, [allowAspectSelection, originalAspect]);

  const computedAspect = useMemo(() => {
    if (!allowAspectSelection && aspect) {
      return aspect;
    }

    if (selectedAspect === 'free') {
      return undefined;
    }

    if (selectedAspect === 'original') {
      return originalAspect || undefined;
    }

    if (typeof selectedAspect === 'number') {
      return selectedAspect;
    }

    return aspect;
  }, [allowAspectSelection, aspect, originalAspect, selectedAspect]);

  const handleCropComplete = useCallback((_, areaPixels) => {
    setCroppedAreaPixels(areaPixels);
  }, []);

  const handleMediaLoaded = useCallback((mediaSize) => {
    if (mediaSize?.naturalWidth && mediaSize?.naturalHeight) {
      setOriginalAspect(mediaSize.naturalWidth / mediaSize.naturalHeight);
    } else {
      setOriginalAspect(null);
    }
  }, []);

  const handleApply = async () => {
    if (!imageSrc) return;

    setProcessing(true);
    try {
      const blob = await getCroppedBlob(imageSrc, croppedAreaPixels);
      onApply?.(blob);
    } catch (error) {
      console.error('Failed to crop image', error);
      onCancel?.();
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Modal
      open={open}
      title={title}
      onCancel={onCancel}
      width={720}
      destroyOnClose
      footer={[
        <Button key="original" onClick={onUseOriginal} disabled={!onUseOriginal}>
          Use Original
        </Button>,
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button key="apply" type="primary" onClick={handleApply} loading={processing} disabled={!imageSrc}>
          Apply Crop
        </Button>
      ]}
    >
      <div
        style={{
          position: 'relative',
          width: '100%',
          height: 360,
          background: '#000',
          borderRadius: 8,
          overflow: 'hidden'
        }}
      >
        {imageSrc && (
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={computedAspect}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
            onMediaLoaded={handleMediaLoaded}
          />
        )}
      </div>

      <Space direction="vertical" style={{ width: '100%', marginTop: 16 }} size="large">
        {instructions && (
          <Text type="secondary">
            {instructions}
          </Text>
        )}

        {/* Warn if original image is square but 16:9 is required */}
        {originalAspect && isSquareAspect(originalAspect) && aspect && aspect > 1.5 && (
          <Alert
            type="warning"
            showIcon
            message="Square image detected"
            description="Your image appears to be square (1:1). For best results with WhatsApp templates, use a landscape image (16:9). The cropper will help adjust, but key content may be cut off."
          />
        )}

        <div>
          <Text strong style={{ display: 'block', marginBottom: 8 }}>Zoom</Text>
          <Slider
            min={1}
            max={3}
            step={0.1}
            value={zoom}
            onChange={setZoom}
          />
        </div>

        {allowAspectSelection && (
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8 }}>Aspect Ratio</Text>
            <Select
              value={allowAspectSelection ? selectedAspect : aspect}
              onChange={setSelectedAspect}
              style={{ width: '100%' }}
              options={aspectOptions}
            />
          </div>
        )}
      </Space>
    </Modal>
  );
};

export default ImageCropModal;
